import axiosInstance from 'api/axiosConfig'
import { ENDPOINTS } from 'api/endpoints'
import { requestEmailConfirmation } from 'api/services/emailConfirmService'
import { getToken, getUser, setToken, removeToken } from 'api/utils/AuthManager'
import {
  clearListingOwnerIntent,
  markListingOwnerIntent,
} from 'api/utils/pendingCreateAdFlow'
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { auth } from '../../firebase'

type AuthOptions = {
  /** Create-ad publisher: bootstrap owner role before any tenant default. */
  asOwner?: boolean
  allowRedirect?: boolean
}

interface AuthContextType {
  user: any
  token: string | null
  login: (email: string, password: string, options?: AuthOptions) => Promise<void>
  register: (email: string, password: string, options?: AuthOptions) => Promise<void>
  loginWithGoogle: (options?: AuthOptions) => Promise<void>
  loginWithFacebook: (options?: AuthOptions) => Promise<void>
  loginWithMicrosoft: (options?: AuthOptions) => Promise<void>
  logout: (redirectTo?: string) => void | Promise<void>
  isAuthenticated: boolean
  validateToken: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setAuthToken] = useState<string | null>(getToken())
  const [user, setUser] = useState<any>(() => getUser())

  const buildMinimalUser = (uid: string, email?: string | null) => ({
    uid,
    id: uid,
    email: email ?? undefined,
  })

  // Handle return from signInWithRedirect (e.g. after production build when popup is blocked)
  useEffect(() => {
    getRedirectResult(auth)
      .then(async result => {
        if (result?.user) {
          const idToken = await result.user.getIdToken()
          setToken(idToken)
          setAuthToken(idToken)
          try {
            const res = await axiosInstance.get(ENDPOINTS.AUTH.ME, {
              headers: { Authorization: `Bearer ${idToken}` },
            })
            setUser(res.data)
            localStorage.setItem('user', JSON.stringify(res.data))
          } catch {
            const minimal = buildMinimalUser(result.user.uid, result.user.email)
            setUser(minimal)
            localStorage.setItem('user', JSON.stringify(minimal))
          }
        }
      })
      .catch(() => {
        // Ignore redirect errors (e.g. user cancelled or no redirect pending)
      })
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken()
        await setUserFromCredential(idToken, firebaseUser.uid, firebaseUser.email ?? null)
      } else {
        removeToken()
        setAuthToken(null)
        setUser(null)
      }
    })

    return () => unsubscribe()
  }, [])

  const setUserFromCredential = async (idToken: string, uid: string, email: string | null) => {
    setToken(idToken)
    setAuthToken(idToken)
    const authHeader = { headers: { Authorization: `Bearer ${idToken}` } }

    try {
      const res = await axiosInstance.get(ENDPOINTS.AUTH.ME, authHeader)
      setUser(res.data)
      localStorage.setItem('user', JSON.stringify(res.data))
      return
    } catch {
      // Fall back to the profile endpoint, then minimal Firebase identity.
    }

    try {
      const res = await axiosInstance.get(ENDPOINTS.USERS.LOAD_USER, authHeader)
      setUser(res.data)
      localStorage.setItem('user', JSON.stringify(res.data))
      return
    } catch {
      // Keep minimal session so protected pages can still load user-scoped data.
    }

    const minimal = buildMinimalUser(uid, email)
    setUser(minimal)
    localStorage.setItem('user', JSON.stringify(minimal))
  }

  const bootstrapOwnerRole = async (idToken: string) => {
    markListingOwnerIntent()
    try {
      // GET + query intent — avoids CORS block on Hostinger (no custom headers).
      await axiosInstance.get(ENDPOINTS.AUTH.ME_PERMISSIONS, {
        headers: { Authorization: `Bearer ${idToken}` },
        params: { intent: 'list_property' },
      })
    } catch {
      // createProperty also promotes owner; do not block auth.
    }
  }

  const login = async (email: string, password: string, options?: AuthOptions) => {
    if (options?.asOwner) markListingOwnerIntent()
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    const idToken = await userCredential.user.getIdToken()
    await setUserFromCredential(idToken, userCredential.user.uid, userCredential.user.email ?? null)
    if (options?.asOwner) {
      await bootstrapOwnerRole(idToken)
    }
  }

  const register = async (email: string, password: string, options?: AuthOptions) => {
    // Mark before Firebase auth so PermissionsContext never GETs as tenant.
    if (options?.asOwner) markListingOwnerIntent()
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const idToken = await userCredential.user.getIdToken()
    await setUserFromCredential(idToken, userCredential.user.uid, userCredential.user.email ?? null)
    if (options?.asOwner) {
      await bootstrapOwnerRole(idToken)
    }
    try {
      // force:false — never rotate an existing pending token (avoids invalidating the first link).
      await requestEmailConfirmation({ email, force: false })
    } catch {
      // Registration succeeds even if confirmation email fails (SMTP outage).
    }
  }

  const isPopupBlockedOrFailed = (err: unknown): boolean => {
    const code = (err as { code?: string })?.code
    const message = (err as { message?: string })?.message ?? ''
    return (
      code === 'auth/popup-blocked' ||
      code === 'auth/cancelled-popup-request' ||
      /popup|cross-origin|opener/i.test(message)
    )
  }

  const signInWithProvider = async (
    provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider,
    options?: AuthOptions
  ) => {
    if (options?.asOwner) markListingOwnerIntent()
    try {
      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await userCredential.user.getIdToken()
      await setUserFromCredential(
        idToken,
        userCredential.user.uid,
        userCredential.user.email ?? null
      )
      if (options?.asOwner) {
        await bootstrapOwnerRole(idToken)
      }
    } catch (err) {
      // Redirect leaves the current page (e.g. create-ad) and can drop draft UI.
      if (isPopupBlockedOrFailed(err) && options?.allowRedirect !== false) {
        await signInWithRedirect(auth, provider)
        // Page will redirect; getRedirectResult() handles the return
      } else {
        throw err
      }
    }
  }

  const loginWithGoogle = async (options?: AuthOptions) => {
    await signInWithProvider(new GoogleAuthProvider(), options)
  }

  const loginWithFacebook = async (options?: AuthOptions) => {
    await signInWithProvider(new FacebookAuthProvider(), options)
  }

  const loginWithMicrosoft = async (options?: AuthOptions) => {
    await signInWithProvider(new OAuthProvider('microsoft.com'), options)
  }

  const logout = async (redirectTo?: string) => {
    try {
      await signOut(auth)
    } catch {
      // Still clear local session if Firebase sign-out fails.
    }
    clearListingOwnerIntent()
    removeToken()
    localStorage.removeItem('user')
    setAuthToken(null)
    setUser(null)

    // Default: always leave protected screens. Only explicit /auth* overrides
    // (e.g. confirm-email mismatch → sign in with the confirmed address).
    if (redirectTo === '/auth' || redirectTo?.startsWith('/auth?')) {
      window.location.assign(redirectTo)
      return
    }

    window.location.assign('/')
  }

  const validateToken = useCallback(async (): Promise<boolean> => {
    await auth.authStateReady()

    const firebaseUser = auth.currentUser
    if (firebaseUser) {
      const idToken = await firebaseUser.getIdToken()
      await setUserFromCredential(idToken, firebaseUser.uid, firebaseUser.email ?? null)
      return true
    }

    const idToken = getToken()
    if (!idToken) {
      removeToken()
      setAuthToken(null)
      setUser(null)
      return false
    }

    try {
      setToken(idToken)
      setAuthToken(idToken)

      const res = await axiosInstance.get(ENDPOINTS.AUTH.ME, {
        headers: { Authorization: `Bearer ${idToken}` },
      })
      setUser(res.data)
      localStorage.setItem('user', JSON.stringify(res.data))
      return true
    } catch {
      removeToken()
      setAuthToken(null)
      setUser(null)
      return false
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        register,
        loginWithGoogle,
        loginWithFacebook,
        loginWithMicrosoft,
        logout,
        isAuthenticated: !!token && !!user,
        validateToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
